export type PaymentErrorSeverity = 'error' | 'warning';

export interface NormalizedPaymentError {
  titleKey: string;
  messageKey: string;
  fallbackMessage: string;
  severity: PaymentErrorSeverity;
}

const PAYMENT_ERROR_TITLE_KEY = 'PAYMENT_ERRORS.TITLE';
const GENERIC_PAYMENT_ERROR_KEY = 'PAYMENT_ERRORS.GENERIC';
const DEFAULT_PAYMENT_ERROR_MESSAGE =
  'Something went wrong while processing your payment. Please try again.';

const STRIPE_ERROR_MAP: Record<string, string> = {
  card_declined: 'PAYMENT_ERRORS.CARD_DECLINED',
  generic_decline: 'PAYMENT_ERRORS.CARD_DECLINED',
  do_not_honor: 'PAYMENT_ERRORS.CARD_DECLINED',
  fraudulent: 'PAYMENT_ERRORS.CARD_DECLINED',
  lost_card: 'PAYMENT_ERRORS.CARD_DECLINED',
  stolen_card: 'PAYMENT_ERRORS.CARD_DECLINED',
  pickup_card: 'PAYMENT_ERRORS.CARD_DECLINED',

  insufficient_funds: 'PAYMENT_ERRORS.INSUFFICIENT_FUNDS',

  expired_card: 'PAYMENT_ERRORS.EXPIRED_CARD',

  incorrect_cvc: 'PAYMENT_ERRORS.INCORRECT_CVC',
  invalid_cvc: 'PAYMENT_ERRORS.INCORRECT_CVC',

  processing_error: 'PAYMENT_ERRORS.PROCESSING_ERROR',

  authentication_required: 'PAYMENT_ERRORS.AUTHENTICATION_REQUIRED',

  card_velocity_exceeded: 'PAYMENT_ERRORS.CARD_VELOCITY_EXCEEDED',

  testmode_decline: 'PAYMENT_ERRORS.REAL_CARD_IN_TEST_MODE',
};

export function normalizePaymentError(error: unknown): NormalizedPaymentError {
  const fallbackMessage =
    extractStripeMessage(error) || DEFAULT_PAYMENT_ERROR_MESSAGE;

  const declineCode = normalizeStripeCode(extractStripeDeclineCode(error));
  const code = normalizeStripeCode(extractStripeCode(error));

  const mappedKey =
    getMappedPaymentErrorKey(declineCode) ||
    getMappedPaymentErrorKey(code);

  if (mappedKey) {
    return buildError(
      mappedKey,
      fallbackMessage,
      mappedKey === 'PAYMENT_ERRORS.REAL_CARD_IN_TEST_MODE'
        ? 'warning'
        : 'error'
    );
  }

  return buildError(GENERIC_PAYMENT_ERROR_KEY, fallbackMessage);
}

function buildError(
  messageKey: string,
  fallbackMessage: string,
  severity: PaymentErrorSeverity = 'error'
): NormalizedPaymentError {
  return {
    titleKey: PAYMENT_ERROR_TITLE_KEY,
    messageKey,
    fallbackMessage,
    severity,
  };
}

function getMappedPaymentErrorKey(code: string | null): string | null {
  if (!code) {
    return null;
  }

  return STRIPE_ERROR_MAP[code] ?? null;
}

function normalizeStripeCode(code: unknown): string | null {
  if (typeof code !== 'string') {
    return null;
  }

  const normalizedCode = code.trim().toLowerCase();

  return normalizedCode || null;
}

function extractStripeMessage(error: unknown): string {
  const value = error as any;

  return (
    value?.message ||
    value?.error?.message ||
    value?.stripeError?.message ||
    value?.setup_intent?.last_setup_error?.message ||
    value?.setupIntent?.last_setup_error?.message ||
    value?.payment_intent?.last_payment_error?.message ||
    value?.paymentIntent?.last_payment_error?.message ||
    value?.invoice?.payment_intent?.last_payment_error?.message ||
    value?.invoice?.paymentIntent?.last_payment_error?.message ||
    value?.response?.error?.message ||
    value?.response?.data?.error?.message ||
    value?.response?.data?.message ||
    ''
  );
}

function extractStripeDeclineCode(error: unknown): string | null {
  const value = error as any;

  return (
    value?.decline_code ||
    value?.declineCode ||
    value?.error?.decline_code ||
    value?.error?.declineCode ||
    value?.stripeError?.decline_code ||
    value?.stripeError?.declineCode ||
    value?.setup_intent?.last_setup_error?.decline_code ||
    value?.setupIntent?.last_setup_error?.decline_code ||
    value?.payment_intent?.last_payment_error?.decline_code ||
    value?.paymentIntent?.last_payment_error?.decline_code ||
    value?.invoice?.payment_intent?.last_payment_error?.decline_code ||
    value?.invoice?.paymentIntent?.last_payment_error?.decline_code ||
    value?.response?.data?.error?.decline_code ||
    value?.response?.data?.error?.declineCode ||
    null
  );
}

function extractStripeCode(error: unknown): string | null {
  const value = error as any;

  return (
    value?.code ||
    value?.error?.code ||
    value?.stripeError?.code ||
    value?.setup_intent?.last_setup_error?.code ||
    value?.setupIntent?.last_setup_error?.code ||
    value?.payment_intent?.last_payment_error?.code ||
    value?.paymentIntent?.last_payment_error?.code ||
    value?.invoice?.payment_intent?.last_payment_error?.code ||
    value?.invoice?.paymentIntent?.last_payment_error?.code ||
    value?.response?.data?.error?.code ||
    null
  );
}
